import { useEffect, useRef } from 'react';
import { audio } from '../audio/engine';
import { MotionIcon, SoundIcon } from './AvControls';
import { motionKind, prefersReducedMotion, requestGyro, setPrefs } from '../audio/prefs';

interface Props {
  onClose: () => void;
}

const MOTION_TEXT = {
  ios: 'Tilt your phone to look around each painted scene. Safari will ask once for motion access.',
  phone: 'Tilt your phone to look around each painted scene.',
  desktop: 'The scene follows your mouse, so each painting shifts in depth as you look around.',
};

/** First-visit card: one tap unlocks Web Audio and (on iOS) gyroscope access in the same gesture. */
export function StartPrompt({ onClose }: Props) {
  const reduced = prefersReducedMotion();
  const kind = motionKind();
  const primary = useRef<HTMLButtonElement>(null);
  useEffect(() => primary.current?.focus(), []);

  const enable = () => {
    // Both calls happen synchronously inside this tap, which is what iOS requires.
    setPrefs({ decided: true, sound: true, motion: !reduced });
    audio.unlock();
    if (!reduced) void requestGyro().then((ok) => setPrefs({ motion: ok }));
    audio.playSfx('ui_click');
    onClose();
  };
  const skip = () => {
    setPrefs({ decided: true, sound: false, motion: false });
    onClose();
  };

  return (
    <div className="start-prompt" role="dialog" aria-modal="true" aria-labelledby="start-prompt-title" aria-describedby="start-prompt-body">
      <div className="start-prompt__card">
        <p className="eyebrow">Before the narrator begins</p>
        <h2 id="start-prompt-title">Play with sound and motion</h2>
        <div id="start-prompt-body" className="start-prompt__body">
          <p className="start-prompt__item">
            <span className="start-prompt__icon" aria-hidden="true"><SoundIcon on /></span>
            <span>
              <strong>Sound.</strong> A candlelit score that shifts with the mood, card and ink effects, and the Narrator reading his outline aloud (until you ruin it).
            </span>
          </p>
          <p className="start-prompt__item">
            <span className="start-prompt__icon" aria-hidden="true"><MotionIcon on={!reduced} /></span>
            <span>
              <strong>Motion.</strong>{' '}
              {reduced ? 'Your device asks for reduced motion, so motion stays off. You can turn it on later from the controls in the corner.' : MOTION_TEXT[kind]}
            </span>
          </p>
        </div>
        <div className="start-prompt__actions">
          <button ref={primary} type="button" className="btn btn-primary start-prompt__go" onClick={enable}>
            {reduced ? 'Turn on sound' : 'Turn on sound + motion'}
          </button>
          <button type="button" className="btn btn-ghost" onClick={skip}>
            Continue without
          </button>
        </div>
        <p className="start-prompt__note">You can change this any time with the sound and motion buttons in the top corner.</p>
      </div>
    </div>
  );
}
